import { useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Delete } from 'lucide-react';

interface NumpadProps {
  value: string;
  onChange: (value: string) => void;
  maxValue?: number;
  /** Called when Enter key is pressed on keyboard */
  onEnter?: () => void;
}

const quickAmounts = [500, 1000, 2000, 5000, 10000];

export const Numpad = ({ value, onChange, maxValue, onEnter }: NumpadProps) => {
  const handleDigit = (digit: string) => {
    // Prevent leading zeros
    if (value === '0' && digit === '0') return;
    if (value === '0' && digit !== '.') {
      onChange(digit);
      return;
    }

    // Prevent multiple decimal points
    if (digit === '.' && value.includes('.')) return;

    // Limit decimal places to 2
    if (value.includes('.')) {
      const [, decimal] = value.split('.');
      if (decimal && decimal.length >= 2) return;
    }

    const newValue = value + digit;
    const numValue = parseFloat(newValue);

    // Check max value
    if (maxValue && numValue > maxValue) return;

    onChange(newValue);
  };

  const handleDelete = () => {
    if (value.length <= 1) {
      onChange('0');
    } else {
      onChange(value.slice(0, -1));
    }
  };

  const handleClear = () => {
    onChange('0');
  };

  const handleQuickAmount = (amount: number) => {
    onChange(amount.toString());
  };

  // Keyboard input support
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if user is typing in an input/textarea (other than numpad itself)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    // Digits 0-9
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      handleDigit(e.key);
      return;
    }

    // Decimal point
    if (e.key === '.' || e.key === ',') {
      e.preventDefault();
      handleDigit('.');
      return;
    }

    // Backspace / Delete
    if (e.key === 'Backspace') {
      e.preventDefault();
      handleDelete();
      return;
    }

    // Delete key = clear
    if (e.key === 'Delete') {
      e.preventDefault();
      handleClear();
      return;
    }

    // Escape = clear
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
      return;
    }

    // Enter = confirm
    if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
      return;
    }
  }, [value, onEnter]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const numericValue = parseFloat(value) || 0;

  return (
    <div className="space-y-3">
      {/* Display */}
      <div className="bg-muted border-2 border-border rounded-lg p-4">
        <div className="text-right">
          <p className="text-4xl font-bold text-gray-900 font-mono">
            {new Intl.NumberFormat('ar-DZ', {
              style: 'currency',
              currency: 'DZD',
              minimumFractionDigits: 2,
            }).format(numericValue)}
          </p>
        </div>
      </div>

      {/* Quick Amounts */}
      <div className="grid grid-cols-5 gap-2">
        {quickAmounts.map((amount) => (
          <Button
            key={amount}
            variant="outline"
            size="sm"
            onClick={() => handleQuickAmount(amount)}
            className="text-xs font-medium"
          >
            {amount / 1000}K
          </Button>
        ))}
      </div>

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-2">
        {/* Numbers 1-9 */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
          <Button
            key={digit}
            variant="outline"
            size="lg"
            onClick={() => handleDigit(digit.toString())}
            className="h-14 text-xl font-semibold hover:bg-emerald-50 hover:border-emerald-300"
          >
            {digit}
          </Button>
        ))}

        {/* Bottom row: Clear, 0, Delete */}
        <Button
          variant="outline"
          size="lg"
          onClick={handleClear}
          className="h-14 text-sm font-medium hover:bg-red-50 hover:border-red-300 text-red-600"
        >
          C
        </Button>

        <Button
          variant="outline"
          size="lg"
          onClick={() => handleDigit('0')}
          className="h-14 text-xl font-semibold hover:bg-emerald-50 hover:border-emerald-300"
        >
          0
        </Button>

        <Button
          variant="outline"
          size="lg"
          onClick={handleDelete}
          className="h-14 hover:bg-orange-50 hover:border-orange-300"
        >
          <Delete className="w-5 h-5 text-orange-600" />
        </Button>

        {/* Decimal point - full width */}
        <Button
          variant="outline"
          size="lg"
          onClick={() => handleDigit('.')}
          className="h-14 text-xl font-semibold col-span-3 hover:bg-emerald-50 hover:border-emerald-300"
        >
          .
        </Button>
      </div>
    </div>
  );
};
